// HomeCard.tsx

import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

interface CardProps {
  backgroundImage: string;
  title: string;
  description: string;
  link?: string;
  icon?: React.ReactNode; // Optional icon prop
}

const ParentContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  width: 100%;
`;

const CardContainer = styled.div<{ $backgroundImage: string }>`
  position: relative;
  flex: 1 1 280px;
  max-width: 300px;
  height: 280px;
  margin: 0.5rem;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  background: url(${(props) => props.$backgroundImage}) no-repeat center center;
  background-size: cover;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center; /* Center content vertically */
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5); /* Overlay for text visibility */
    z-index: 1;
  }
`;

const CardContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 1rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;

  h3 {
    font-size: 1.25rem;
    margin-bottom: 0.5rem;
    font-weight: bold;
    color: var(--color-text-light); /* Use light text from theme */
  }

  p {
    font-size: 1rem;
    line-height: 1.5;
    color: var(--color-text-light);
  }

  .icon {
    margin-bottom: 1rem;
    color: var(--color-text-light);
  }
`;

const HomeCard: React.FC<CardProps> = ({ backgroundImage, title, description, link, icon }) => {
  const CardContentWrapper = (
    <CardContainer $backgroundImage={backgroundImage}>
      <CardContent>
        {icon && <div className="icon">{icon}</div>}
        <h3>{title}</h3>
        <p>{description}</p>
      </CardContent>
    </CardContainer>
  );

  return (
    <ParentContainer>
      {link ? (
        <StyledLink to={link}>
          {CardContentWrapper}
        </StyledLink>
      ) : (
        CardContentWrapper
      )}
    </ParentContainer>
  );
};

export default HomeCard;
